import { createContext, useContext, useLayoutEffect, type ReactNode } from 'react';

import { useCloudSynced } from '@/hooks';
import { applySystemBars, applyTelegramChrome, useSystemDark } from '@/platform';

type Theme = 'dark' | 'light';
export type ThemePref = Theme | 'system';

const KEY = 'theme';

const asPref = (v: unknown): ThemePref | null =>
  v === 'dark' || v === 'light' || v === 'system' ? v : null;

interface ThemeState {
  theme: Theme;
  pref: ThemePref;
  setPref: (pref: ThemePref) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeState>({
  theme: 'dark',
  pref: 'system',
  setPref: () => {},
  toggle: () => {},
});

export function useTheme(): ThemeState {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const systemDark = useSystemDark();
  const [pref, setPref] = useCloudSynced<ThemePref>(KEY, asPref, 'system');

  const theme: Theme = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    applySystemBars(theme);
    applyTelegramChrome(theme);
  }, [theme]);

  function toggle() {
    setPref(theme === 'dark' ? 'light' : 'dark');
  }

  return (
    <ThemeContext.Provider value={{ theme, pref, setPref, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}
